import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TranslatedFormMessage } from '@/features/organization-users/components/translated-form-message'
import {
  inviteOrganizationUserFormSchema,
  type InviteOrganizationUserFormData,
} from '@/features/organization-users/components/invite-organization-user.schema'
import {
  useInviteOrganizationUser,
  useOrganizationUserRoles,
} from '@/features/organization-users/hooks/use-organization-users-api'

const defaultFormValues: InviteOrganizationUserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  roleSlug: '',
}

type InviteOrganizationUserDialogProps = {
  organizationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function InviteOrganizationUserDialog({
  organizationId,
  open,
  onOpenChange,
}: InviteOrganizationUserDialogProps) {
  const { t } = useTranslation(['organizationUsers', 'common'])
  const { data: roles } = useOrganizationUserRoles(organizationId)
  const {
    mutateAsync: inviteOrganizationUser,
    isPending: isInviting,
    error: inviteError,
    reset: resetMutation,
  } = useInviteOrganizationUser(organizationId)

  const form = useForm<InviteOrganizationUserFormData>({
    resolver: zodResolver(inviteOrganizationUserFormSchema),
    defaultValues: defaultFormValues,
  })

  useEffect(() => {
    if (open) {
      form.reset(defaultFormValues)
      resetMutation()
    }
  }, [open, form, resetMutation])

  async function onSubmit(data: InviteOrganizationUserFormData) {
    try {
      await inviteOrganizationUser(data)
      toast.success(t('organizationUsers:inviteUserDialog.success'))
      onOpenChange(false)
    } catch (submitError) {
      if (submitError instanceof Error && submitError.message === 'CONFLICT') {
        return
      }
      throw submitError
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        showCloseButton={!isInviting}
      >
        <DialogHeader>
          <DialogTitle>{t('organizationUsers:inviteUserDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('organizationUsers:inviteUserDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid gap-4"
            id="invite-organization-user-form"
          >
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('organizationUsers:inviteUserDialog.fields.firstName.label')}
                  </FormLabel>
                  <FormControl>
                    <Input autoComplete="given-name" {...field} />
                  </FormControl>
                  <TranslatedFormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('organizationUsers:inviteUserDialog.fields.lastName.label')}
                  </FormLabel>
                  <FormControl>
                    <Input autoComplete="family-name" {...field} />
                  </FormControl>
                  <TranslatedFormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('organizationUsers:inviteUserDialog.fields.email.label')}
                  </FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="email" {...field} />
                  </FormControl>
                  <TranslatedFormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="roleSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('organizationUsers:permissionsDialog.fields.selectRole')}
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t(
                            'organizationUsers:permissionsDialog.fields.selectRole'
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roles?.map((role) => (
                        <SelectItem key={role} value={role}>
                          {t(`organizationUsers:roles.${role}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <TranslatedFormMessage />
                </FormItem>
              )}
            />
            {inviteError?.message === 'CONFLICT' && (
              <Alert variant="destructive">
                <AlertDescription>
                  {t('organizationUsers:inviteUserDialog.alerts.conflict')}
                </AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isInviting}
                onClick={() => {
                  onOpenChange(false)
                }}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isInviting}>
                {isInviting ? <Loader2 className="animate-spin" /> : null}
                {t('common:actions.add')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
